<template lang="pug">
div
  slot(name="custom-save")
  liondesk-dialog(
    v-model="showDeleteDialog"
    :dialog-title="'DELETE ' + singularResource"
    :disable-ok-button="!hasConfirmedDelete"
    @click:ok="handleConfirmDelete"
    @click:cancel="cancel()"
  )
    template(slot="body")
      p(v-if="noContactsMessage") You are permanently deleting "{{ selectedLabel }}".
      p(v-if="!noContactsMessage") You are permanently deleting "{{ selectedLabel }}" from all {{ affectedCount }} contacts it is applied to.

      p(v-if="additionalMessage") {{ additionalMessage }}

      v-text-field(autofocus flat :placeholder="typeDeletePrompt" v-model="confirmDelete")
  liondesk-dialog(
    v-model="showEditDialog"
    :dialog-title="'EDIT ' + singularResource"
    ok-button-text="EDIT"
    @click:ok="handleSaveEdit"
    @click:cancel="showEditDialog = false"
  )
    template(slot="body")
      p(v-if="!noContactsMessage") This {{ singularResource }} is assigned to
        b &nbsp;{{ affectedCount }} contacts
        | &nbsp;which will be affected by the change.
      p(v-else)  This {{ singularResource }} wll be modified:
        b &nbsp; {{ selectedLabel }}
      p Please confirm your change.
  liondesk-dialog(
    v-model="showCancelEditDialog"
    dialog-title="CANCEL EDIT"
    dialogBody="Are you sure you want to discard your changes?"
    ok-button-text="DISCARD"
    @click:ok="handleCancelEdit"
    @click:cancel="showCancelEditDialog = false"
  )
  v-layout.mb-2(row align-center space-between)
    v-flex(xs6)
      v-btn(color="primary" :disabled="shouldDisableAddButton" @click="addNewResource") {{ getAddBtnText }}
      v-btn(v-if="showSortAction" @click="triggerExternalSort")
        v-icon swap_vert
        | Sort list
    v-flex(xs6)
      v-layout(row align-center justify-end)
        v-flex(xs6)
          v-text-field.pa-0(
            single-line
            hide-details
            prepend-inner-icon="search"
            :label="'Search ' + pluralResource"
            @input="handleSearch"
          )
        v-flex.px-2(shrink)
          span.content-description {{ total }} {{ description }}
  v-layout.ml-2.mb-2(row align-center)
    .permission-warning
      v-icon.mr-1(small color="grey") info
      template(v-if="canShowAdminResources && !canManageOwnResources")
        | All {{ pluralResource }} are Managed by your Administrator
      template(v-if="canShowAdminResources && canManageOwnResources")
        | Some {{ pluralResource }} are Managed by your Administrator
  v-card
    v-slide-y-transition
      v-layout.notification.py-2.px-3(
        v-if="showNotification"
        row
        justify-center
        align-center
        :class="messageStyle"
      )
        template(v-if="showSuccessMessage")
          v-icon(color="white") check_circle
          span.pl-2 {{ successMessage }}
        template(v-else-if="showErrorMessage")
          v-icon(color="white") cancel
          span.pl-2 There was an error
    v-data-table.content-table(
      hide-actions
      disable-initial-sort
      :headers="tableHeaders"
      :items="items"
      :search="search"
      :custom-filter="customSearch"
      :item-key="itemKey"
      :loading="isLoading"
      :pagination.sync="pagination"
      :custom-sort="customSorting"
    )
      template(slot="no-data")
        .text-md-center
          v-icon.mt-5(color="grey" x-large) error
        h3.mt-1.mb-5.text-md-center.no-data
          template(v-if="canManageOwnResources") Click the "Add {{ singularResource }}" button to add a field to your contacts!
          template(v-else) Your administrator has not set up {{ pluralResource }} for you.
      template(slot="items" slot-scope="props")
        slot(name="items" :item.sync="props.item")
        td.text-xs-right
          template(v-if="shouldEdit(props.item)")
            v-btn.mx-1(flat icon :disabled="!shouldSave" @click="save(props.item)")
              v-icon(color="grey") check_circle
            v-btn.mx-1(flat icon @click="cancel()")
              v-icon(color="grey") cancel
          template(v-if="showEditDeleteButtons(props.item)")
            v-btn.mx-1(
              v-if="!disableEdit"
              flat
              icon
              :disabled="shoulDisableEditButtton(props.item)"
              @click="edit(props.item)"
            )
              v-icon(color="grey") edit
            v-btn.mx-1(
              flat
              icon
              :disabled="shoulDisableDeleteButtton(props.item)"
              @click="handleDelete(props.item)"
            )
              v-icon(color="grey") delete
          template(v-if="isItemReadOnly(props.item)")
            span {{ getReadOnlyLabel(props.item) }}
  v-layout.pagination
    v-flex.pages
      v-pagination(
        v-if="totalPages > 1"
        v-model="pagination.page"
        :length="totalPages"
        :total-visible="7"
      )
</template>

<script>
import { debounce } from '@/Core/utilities';
import LiondeskDialog from '@/Core/components/LiondeskDialog';

const states = {
  EDITING: 'editing',
  CREATING: 'creating',
  DELETING: 'deleting',
  NONE: 'none',
};

export default {
  name: 'ResourceTable',
  components: {
    LiondeskDialog,
  },
  props: {
    addBtnText: String,
    singularResource: String,
    pluralResource: String,
    placeHolderItem: Object,
    shouldSave: Boolean,
    selectedLabel: String,
    affectedCount: Number,
    additionalMessage: String,
    noContactsMessage: Boolean,
    isLoading: Boolean,
    showSortAction: Boolean,
    disableAddNewButton: Boolean,
    canShowAdminResources: Boolean,
    showSuccessMessage: Boolean,
    showErrorMessage: Boolean,
    itemKey: {
      type: String,
      default: 'id',
    },
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    canManageOwnResources: {
      type: Boolean,
      default: true,
    },
    disableEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: states.NONE,
      lastAction: states.NONE,
      selectedItem: null,
      editItemId: null,
      copyEditItem: null,
      search: '',
      showDeleteDialog: false,
      showEditDialog: false,
      showCancelEditDialog: false,
      typeDeletePrompt: 'Type DELETE to confirm',
      confirmDelete: '',
      maxNonRecentItemId: null,
      copySortedItems: null,
      pagination: {
        page: 1,
        rowsPerPage: 10,
        descending: false,
      },
    };
  },
  watch: {
    state(newValue, oldValue) {
      switch (newValue) {
        case states.DELETING:
          if (oldValue === states.EDITING) {
            this.undo();
            this.editItemId = null;
            this.copyEditItem = null;
          } else if (oldValue === states.CREATING) {
            this.items.splice(0, 1);
          }
          this.showDeleteDialog = true;
          break;
        case states.NONE:
          this.lastAction = oldValue;
          this.editItemId = null;
          this.selectedItem = null;
          this.copyEditItem = null;
          this.showDeleteDialog = false;
          this.showEditDialog = false;
          this.showCancelEditDialog = false;
          this.confirmDelete = '';
          break;
      }
    },
    showErrorMessage(val) {
      if (!val) return;
      setTimeout(() => this.$emit('clear-messages'), 3000);
    },
    showSuccessMessage(val) {
      if (!val) return;
      setTimeout(() => this.$emit('clear-messages'), 3000);
    },
  },
  computed: {
    tableHeaders() {
      return [
        ...this.headers,
        { text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '210px' },
      ];
    },
    // to be able to select specific fields to be filtered
    // add to the headers objects the property 'filterable' as true
    filterableHeaders() {
      return this.headers.filter(h => h.filterable);
    },
    total() {
      return this.items.length;
    },
    description() {
      return this.total === 1 ? this.singularResource : this.pluralResource;
    },
    shouldDisableAddButton() {
      return (
        this.state === states.CREATING ||
        this.state === states.EDITING ||
        this.disableAddNewButton ||
        !this.canManageOwnResources
      );
    },
    hasConfirmedDelete() {
      return this.confirmDelete === 'DELETE';
    },
    totalPages() {
      return Math.ceil(this.total / this.pagination.rowsPerPage);
    },
    showNotification() {
      return this.showSuccessMessage || this.showErrorMessage;
    },
    messageStyle() {
      if (this.showSuccessMessage) return 'success';
      else if (this.showErrorMessage) return 'error';
    },
    successMessage() {
      switch (this.lastAction) {
        case states.CREATING:
          return `${this.singularResource} has been added`;
        case states.EDITING:
          return 'Update successful';
        case states.DELETING:
          return `${this.singularResource} has been deleted`;
        default:
          return 'Success';
      }
    },
    getAddBtnText() {
      if (this.addBtnText) {
        return this.addBtnText;
      }
      return `ADD ${this.singularResource}`;
    },
  },
  mounted() {
    if (!this.items.length) return;
    const maxId = this.items.reduce((maxId, item) => {
      if (item[this.itemKey] > maxId) return item[this.itemKey];
      return maxId;
    }, 0);
    this.maxNonRecentItemId = maxId;
  },
  methods: {
    handleSearch(value) {
      debounce(() => (this.search = value), []);
    },
    customSearch(items, value, search) {
      let headers = this.headers;
      if (this.filterableHeaders.length > 0) headers = this.filterableHeaders;

      return items.filter(i => {
        return headers.some(h => search(i[h.value], value));
      });
    },
    showEditDeleteButtons(item) {
      return !this.isItemReadOnly(item) && (this.state !== states.CREATING || !!item[this.itemKey]);
    },
    shouldEdit(item) {
      if (this.$slots['custom-save']) {
        return false;
      }
      switch (this.state) {
        case states.CREATING:
          return item[this.itemKey] === 0;
        case states.EDITING:
          return item[this.itemKey] === this.editItemId;
        default:
          return false;
      }
    },
    shoulDisableEditButtton(item) {
      return (
        this.state === states.CREATING ||
        !item[this.itemKey] ||
        item[this.itemKey] === this.editItemId
      );
    },
    shoulDisableDeleteButtton(item) {
      return !item[this.itemKey];
    },
    undo() {
      const index = this.items.findIndex(i => i[this.itemKey] === this.editItemId);
      Object.assign(this.items[index], this.copyEditItem);
    },
    addNewResource() {
      if (!this.placeHolderItem) return;
      this.state = states.CREATING;
      if (this.$slots['custom-save']) {
        this.$emit('create');
        this.state = states.NONE;
        return
      }
      this.items.splice(0, 0, Object.assign({}, this.placeHolderItem));
      this.copySortedItems.splice(0, 0, Object.assign({}, this.placeHolderItem));
      this.$emit('create');
    },
    edit(item) {
      if (this.state === states.EDITING) this.undo();
      this.state = states.EDITING;
      if (this.$slots['custom-save']) {
        this.$emit('edit', item[this.itemKey]);
        this.state = states.NONE;
        return
      }
      this.editItemId = item[this.itemKey];
      this.copyEditItem = Object.assign({}, item);
      this.selectedItem = item;
      this.$emit('edit', item[this.itemKey]);
    },
    cancel(isImmediate) {
      if (this.state === states.EDITING) {
        if (isImmediate) this.undo();
        else return (this.showCancelEditDialog = true);
      } else if (this.state === states.CREATING) {
        this.items.splice(0, 1);
      }
      this.state = states.NONE;
      this.$emit('cancel');
    },
    handleSaveEdit() {
      this.$emit('save:edit', this.selectedItem);
      this.state = states.NONE;
    },
    handleCancelEdit() {
      this.undo();
      this.state = states.NONE;
      this.$emit('cancel');
    },
    save(item) {
      if (this.state === states.CREATING) {
        this.$emit('save:create', item);
        this.state = states.NONE;
      } else {
        if (item[this.itemKey] > this.maxNonRecentItemId) return this.handleSaveEdit();
        this.showEditDialog = true;
      }
    },
    handleDelete(item) {
      this.selectedItem = item;
      // if this is a newly created record, skip opening confirmation modal
      if (item[this.itemKey] > this.maxNonRecentItemId) return this.handleConfirmDelete();
      this.state = states.DELETING;
      this.$emit('selected-item', item[this.itemKey]);
    },
    handleConfirmDelete() {
      this.$emit('delete', Object.assign({}, this.selectedItem));
      this.state = states.NONE;
    },
    customSorting(items, index, isDescending) {
      if (this.state === states.CREATING || this.state === states.EDITING)
        return this.copySortedItems;

      this.copySortedItems = items.sort((a, b) => {
        if (!isDescending) {
          return a[index] < b[index] ? -1 : 1;
        } else {
          return b[index] < a[index] ? -1 : 1;
        }
      });

      return this.copySortedItems;
    },
    isItemReadOnly(item) {
      const disabledStatus = ['subadmin', 'system'];
      if (!item.disabled_by) return false;
      return disabledStatus.includes(item.disabled_by);
    },
    triggerExternalSort() {
      this.pagination.descending = false;
      this.pagination.sortBy = this.headers[0].value;
      this.cancel(true);
      this.$emit('sort');
    },
    getReadOnlyLabel(item) {
      if (item.disabled_by === 'subadmin') return 'Managed by Admin';
      return 'System Setting';
    },
  },
};
</script>

<style lang="scss" scoped>
.content-description {
  color: var(--v-text-lighten3);
  font-size: 16px;
}

.permission-warning {
  color: var(--v-text-lighten3);
  font-size: 12px;
}

.content-table /deep/ .v-table__overflow {
  overflow-x: scroll;

  tbody {
    tr:hover {
      box-shadow: none;
    }

    td {
      padding-top: 20px !important;
      padding-bottom: 0px !important;
    }

    td:last-child {
      vertical-align: middle !important;
      padding-top: 0px !important;
    }
  }
}

.no-data {
  color: var(--v-text-lighten3);
  font-size: 18px;
  font-weight: 500;
}

.pagination {
  height: 60px;
}

.notification {
  position: absolute;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 25px !important;
  z-index: 10;

  .success {
    background-color: var(--v-success-base);
  }

  .error {
    background-color: var(--v-error-base);
  }

  span {
    color: white;
    font-size: 14px;
  }
}
</style>
